import { Plus, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/shadcn/button'
import { Checkbox } from '@/components/shadcn/checkbox'
import { Input } from '@/components/shadcn/input'
import { Textarea } from '@/components/shadcn/textarea'
import { IconColorPicker } from '@/components/common/IconColorPicker'
import { HelpLabel } from '@/components/common/help/HelpLabel'
import { HelpText } from '@/components/common/help/HelpText'
import { helpKey } from '@/lib/action-modules/help'
import { TranslateButton } from '@/components/common/translations/TranslateButton'
import type { useEntityTranslations } from '@/components/common/translations/useEntityTranslations'
import type { EditorActionModule } from '@/lib/action-modules/registry/types'

interface Props {
  actionModule: EditorActionModule
  onChanged: (value: Partial<EditorActionModule>) => void
  translations: ReturnType<typeof useEntityTranslations>
  onUploadIcon: (file: File, dataUrl: string) => void
  onDeleteIcon: () => void
  iconBusy: boolean
}

export default function CommonPropertiesTab({ actionModule, onChanged, translations, onUploadIcon, onDeleteIcon, iconBusy }: Props) {
  const { t } = useTranslation('action-modules-wizard')
  const g = (key: string) => t(`tabGeneral.${key}`)
  return (
    <div className="flex flex-col gap-8 p-4">
      {/* Two columns: text fields on the left, icon + communication on the right. */}
      <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-2">
        {/* Left column — module texts */}
        <section className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <HelpLabel helpKey={helpKey('generalName')}>{g('name')}</HelpLabel>
            <div className="flex gap-2">
              <Input value={actionModule.name} onChange={e => onChanged({ name: e.target.value })} />
              <TranslateButton
                dialogTitle={g('nameTranslateTitle')}
                value={translations.get('name')}
                onChange={v => translations.set('name', v)}
              />
            </div>
          </div>
          <Field
            label={g('description')}
            help={helpKey('generalDescription')}
            value={actionModule.description}
            onChange={v => onChanged({ description: v })}
            translateTitle={g('descriptionTranslateTitle')}
            translations={translations.get('description')}
            onTranslationsChange={v => translations.set('description', v)}
          />
          <Field
            label={g('employeeDescription')}
            help={helpKey('generalEmployeeDescription')}
            value={actionModule.employeeDescription}
            onChange={v => onChanged({ employeeDescription: v })}
            translateTitle={g('employeeDescriptionTranslateTitle')}
            translations={translations.get('employeeDescription')}
            onTranslationsChange={v => translations.set('employeeDescription', v)}
          />
          <Field
            label={g('completionDescription')}
            help={helpKey('generalCompletionDescription')}
            value={actionModule.completionDescriptionText}
            onChange={v => onChanged({ completionDescriptionText: v })}
            translateTitle={g('completionDescriptionTranslateTitle')}
            translations={translations.get('completionDescriptionText')}
            onTranslationsChange={v => translations.set('completionDescriptionText', v)}
          />
          {/* OK/cancel button labels are short single-line strings — side by side. */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1">
              <HelpLabel helpKey={helpKey('generalPositiveAnswerText')}>{g('positiveAnswerText')}</HelpLabel>
              <div className="flex gap-2">
                <Input
                  value={actionModule.positiveAnswerText}
                  onChange={e => onChanged({ positiveAnswerText: e.target.value })}
                />
                <TranslateButton
                  dialogTitle={g('positiveAnswerTextTranslateTitle')}
                  value={translations.get('positiveAnswerText')}
                  onChange={v => translations.set('positiveAnswerText', v)}
                />
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <HelpLabel helpKey={helpKey('generalNegativeAnswerText')}>{g('negativeAnswerText')}</HelpLabel>
              <div className="flex gap-2">
                <Input
                  value={actionModule.negativeAnswerText}
                  onChange={e => onChanged({ negativeAnswerText: e.target.value })}
                />
                <TranslateButton
                  dialogTitle={g('negativeAnswerTextTranslateTitle')}
                  value={translations.get('negativeAnswerText')}
                  onChange={v => translations.set('negativeAnswerText', v)}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Right column — icon + communication */}
        <div className="flex flex-col gap-6">
          {/* Icon upload only works once the module exists (needs an id). */}
          {/* IconColorPicker is shared with the service-module editor and renders its own label,
              so its help is a visible paragraph below instead of a hint on the label. */}
          <div className="flex flex-col gap-1">
            <IconColorPicker
              iconName={actionModule.iconName || null}
              colorHex={actionModule.colorHex || '#10b981'}
              iconUrl={actionModule.iconUrl}
              busy={iconBusy}
              onIconChange={name => onChanged({ iconName: name })}
              onColorChange={hex => onChanged({ colorHex: hex })}
              onUpload={onUploadIcon}
              onDelete={onDeleteIcon}
            />
            <HelpText helpKey={helpKey('generalIcon')} />
          </div>

          <section className="flex flex-col gap-3">
            <h3 className="text-sm font-semibold">{g('titleCommunication')}</h3>
            <div className="flex flex-col gap-2">
              <HelpLabel
                helpKey={helpKey('generalDetailedEmailNotification')}
                labelClassName="flex items-center gap-2 font-normal"
              >
                <Checkbox
                  checked={actionModule.detailedEmailNotification}
                  onCheckedChange={c => onChanged({ detailedEmailNotification: c === true })}
                />
                {g('detailedEmailNotification')}
              </HelpLabel>
              <HelpLabel
                helpKey={helpKey('generalSummaryEmailNotification')}
                labelClassName="flex items-center gap-2 font-normal"
              >
                <Checkbox
                  checked={actionModule.summaryEmailNotification}
                  onCheckedChange={c => onChanged({ summaryEmailNotification: c === true })}
                />
                {g('summaryEmailNotification')}
              </HelpLabel>
            </div>
            <div className="flex flex-col gap-2">
              <EmailAddressList
                addresses={actionModule.emailNotificationAddresses}
                onChanged={next => onChanged({ emailNotificationAddresses: next })}
              />
              <HelpText helpKey={helpKey('generalEmailNotificationAddresses')} />
            </div>
          </section>
        </div>
      </div>

      {/* Advanced settings span the full width below both columns. */}
      <section className="flex flex-col gap-3">
        <h3 className="text-sm font-semibold">{g('titleExtendedSettings')}</h3>
        <HelpLabel
          helpKey={helpKey('generalCanCompleteMultipleTimes')}
          labelClassName="flex items-center gap-2 font-normal"
        >
          <Checkbox
            checked={actionModule.canCompleteMultipleTimes}
            onCheckedChange={c => onChanged({ canCompleteMultipleTimes: c === true })}
          />
          {g('canCompleteMultipleTimes')}
        </HelpLabel>
        <div className="flex max-w-md flex-col gap-1">
          <HelpLabel helpKey={helpKey('generalMaxUserCanComplete')}>{g('maxUserCanComplete')}</HelpLabel>
          <Input
            type="number"
            value={actionModule.maxUserCanComplete ?? ''}
            onChange={e => onChanged({ maxUserCanComplete: e.target.value !== '' ? Number(e.target.value) : null })}
          />
        </div>
      </section>
    </div>
  )
}

/** Repeatable list of notification email addresses: one input per address with a remove
 *  button, plus an "add" button. Blank rows are allowed while editing and dropped on save
 *  (see `buildRequest`). */
function EmailAddressList({
  addresses,
  onChanged,
}: {
  addresses: string[]
  onChanged: (next: string[]) => void
}) {
  const { t } = useTranslation('action-modules-wizard')
  const g = (key: string) => t(`tabGeneral.${key}`)

  const update = (index: number, value: string) =>
    onChanged(addresses.map((a, i) => (i === index ? value : a)))
  const remove = (index: number) => onChanged(addresses.filter((_, i) => i !== index))
  const add = () => onChanged([...addresses, ''])

  return (
    <div className="flex flex-col gap-2">
      {addresses.length === 0 ? (
        <p className="text-sm text-muted-foreground">{g('noEmailNotificationAddresses')}</p>
      ) : (
        addresses.map((address, index) => (
          <div key={index} className="flex items-center gap-2">
            <Input
              type="email"
              value={address}
              placeholder={g('emailNotificationAddressPlaceholder')}
              onChange={e => update(index, e.target.value)}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="shrink-0"
              aria-label={g('removeEmail')}
              onClick={() => remove(index)}
            >
              <X className="size-4" />
            </Button>
          </div>
        ))
      )}
      <Button type="button" variant="outline" size="sm" className="self-start" onClick={add}>
        <Plus className="size-4" />
        {g('addEmail')}
      </Button>
    </div>
  )
}

function Field({
  label,
  help,
  value,
  onChange,
  translateTitle,
  translations,
  onTranslationsChange,
}: {
  label: string
  help: string
  value: string
  onChange: (v: string) => void
  translateTitle: string
  translations: Record<string, string>
  onTranslationsChange: (v: Record<string, string>) => void
}) {
  return (
    <div className="flex flex-col gap-1">
      <HelpLabel helpKey={help}>{label}</HelpLabel>
      <div className="flex items-start gap-2">
        <Textarea value={value} onChange={e => onChange(e.target.value)} rows={2} />
        <TranslateButton dialogTitle={translateTitle} value={translations} onChange={onTranslationsChange} />
      </div>
    </div>
  )
}
